import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  CheckCircle, 
  XCircle, 
  Loader2, 
  ArrowRight,
  Link2,
  RefreshCw,
  Mail
} from 'lucide-react';
import { API } from '../lib/api';

/**
 * Magic Link Login Page - B5 Implementation
 * 
 * User lands here after clicking magic link in email.
 * URL: /magic-login?token=xxx
 */
export default function MagicLoginPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  
  const [status, setStatus] = useState('validating'); // validating, consuming, success, error
  const [error, setError] = useState(null);
  
  const token = searchParams.get('token');
  
  useEffect(() => {
    if (!token) {
      setStatus('error');
      setError(language === 'en' 
        ? 'Access token missing from URL'
        : 'Token di accesso mancante nell\'URL');
      return;
    }
    
    validateAndConsume();
  }, [token]);
  
  const validateAndConsume = async () => {
    setStatus('validating');
    
    // First validate
    try {
      const validateResponse = await axios.get(`${API}/guest/validate-magic-link?token=${token}`);
      
      if (!validateResponse.data.valid) {
        setStatus('error');
        setError(validateResponse.data.reason);
        return;
      }
    } catch (err) {
      setStatus('error');
      setError(language === 'en' 
        ? 'Unable to validate link'
        : 'Impossibile validare il link');
      return;
    }
    
    // Token is valid, consume it
    setStatus('consuming');
    
    try {
      const consumeResponse = await axios.post(`${API}/guest/consume-magic-link`, {
        token: token
      });
      
      if (consumeResponse.data.success) {
        // Store token and user
        localStorage.setItem('token', consumeResponse.data.token);
        localStorage.setItem('user', JSON.stringify(consumeResponse.data.user));
        
        setStatus('success');
        toast.success(language === 'en' 
          ? 'Login successful!'
          : 'Accesso effettuato!');
        
        // Redirect to dashboard after short delay
        setTimeout(() => {
          navigate('/dashboard');
        }, 1500);
      } else {
        setStatus('error');
        setError(consumeResponse.data.error);
      }
    } catch (err) {
      setStatus('error');
      setError(err.response?.data?.detail || err.message);
    }
  };
  
  const renderValidating = () => (
    <div className="text-center py-12">
      <div className="w-20 h-20 mx-auto bg-gradient-to-br from-[#C5A059] to-[#B8934A] rounded-full flex items-center justify-center mb-6">
        <Loader2 className="w-10 h-10 text-white animate-spin" />
      </div>
      <h1 className="text-2xl font-cinzel font-semibold text-gray-900 mb-3">
        {language === 'en' ? 'Verifying link...' : 'Verifica link...'}
      </h1>
      <p className="text-gray-600">
        {language === 'en' ? 'Please wait a moment' : 'Attendere un momento'}
      </p>
    </div>
  );
  
  const renderConsuming = () => (
    <div className="text-center py-12">
      <div className="w-20 h-20 mx-auto bg-gradient-to-br from-[#1B4332] to-[#2D5A45] rounded-full flex items-center justify-center mb-6">
        <Loader2 className="w-10 h-10 text-white animate-spin" />
      </div>
      <h1 className="text-2xl font-cinzel font-semibold text-gray-900 mb-3">
        {language === 'en' ? 'Logging you in...' : 'Accesso in corso...'}
      </h1>
      <p className="text-gray-600">
        {language === 'en' ? 'Creating your session' : 'Creazione sessione'}
      </p>
    </div>
  );
  
  const renderSuccess = () => (
    <div className="text-center py-12">
      <div className="w-20 h-20 mx-auto bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center mb-6 shadow-lg">
        <CheckCircle className="w-10 h-10 text-white" />
      </div>
      <h1 className="text-2xl font-cinzel font-semibold text-gray-900 mb-3">
        {language === 'en' ? 'Welcome!' : 'Benvenuto!'}
      </h1>
      <p className="text-gray-600 mb-6">
        {language === 'en' 
          ? 'You are being redirected to your dashboard...'
          : 'Reindirizzamento alla dashboard...'}
      </p>
      
      <Link to="/dashboard">
        <Button className="bg-[#1B4332] hover:bg-[#2D5A45] text-white">
          {language === 'en' ? 'Go to Dashboard' : 'Vai alla Dashboard'}
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </Link>
    </div>
  );
  
  const renderError = () => (
    <div className="text-center py-12">
      <div className="w-20 h-20 mx-auto bg-gradient-to-br from-red-500 to-red-600 rounded-full flex items-center justify-center mb-6 shadow-lg">
        <XCircle className="w-10 h-10 text-white" />
      </div>
      <h1 className="text-2xl font-cinzel font-semibold text-gray-900 mb-3">
        {language === 'en' ? 'Link Invalid' : 'Link Non Valido'}
      </h1>
      <p className="text-gray-600 mb-2">
        {error || (language === 'en' 
          ? 'This access link is invalid or has expired.'
          : 'Questo link di accesso non è valido o è scaduto.')}
      </p>
      <p className="text-sm text-gray-500 mb-8">
        {language === 'en'
          ? 'Magic links expire after 15 minutes.'
          : 'I link di accesso scadono dopo 15 minuti.'}
      </p>
      
      <div className="space-y-3">
        <Link to="/login">
          <Button className="w-full max-w-xs bg-[#1B4332] hover:bg-[#2D5A45] text-white">
            <Mail className="w-4 h-4 mr-2" />
            {language === 'en' ? 'Request New Link' : 'Richiedi Nuovo Link'}
          </Button>
        </Link>
        
        <Button 
          variant="outline" 
          onClick={() => validateAndConsume()}
          className="w-full max-w-xs border-gray-300 text-gray-700"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          {language === 'en' ? 'Try Again' : 'Riprova'}
        </Button>
      </div>
    </div>
  );
  
  return (
    <div className="min-h-screen bg-[#F9F9F7] flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-xl border-0">
        <CardContent className="p-8">
          {status === 'validating' && renderValidating()}
          {status === 'consuming' && renderConsuming()}
          {status === 'success' && renderSuccess()}
          {status === 'error' && renderError()}
        </CardContent>
      </Card>
    </div>
  );
}
